import moment from 'moment';
import { dispatch } from 'src/redux/store';
import { getPracticeId } from 'src/redux/slices/clincheck';
import { getTaskWidgetCounter, getTreatmentWidgetPrice, getWidgetCounter, startWidgetLoading } from 'src/redux/slices/widgetCounter';
import { getReportsAppointmentListing } from 'src/redux/slices/analytics';
import { useGetTreatment } from './useGetTreatment';

// Default date range for appointment-based widgets when no dates are provided (last 90 days)
const getDefaultAppointmentDateRange = () => {
    const end = moment().format('YYYY-MM-DD');
    const start = moment().subtract(90, 'days').format('YYYY-MM-DD');
    return `${start},${end}`;
};

export default function useReloadWidgetCounter() {
    const { getTreatmentId } = useGetTreatment();


    const reloadWidgetCounter = ({ array, startDate, endDate, isAppointmentDate, isDate, taskFilter, acrossPractice, practiceArray }) => {
        if (getPracticeId()) {
            // Signal start of a new loading cycle for skeleton transitions
            dispatch(startWidgetLoading(array.length));
            array.forEach((data) => {
                const detail = {
                    key: data.key
                };
                detail.acrossPractice = acrossPractice;
                detail.practiceArray = practiceArray || "";
                if (data.status) {
                    detail.status = data.status;
                }
                if (data.treatmentStatus) {
                    detail.treatmentStatus = data.treatmentStatus;
                }
                if ((isDate || data.isDate) && startDate && endDate) {
                    detail.dateRange = `${startDate},${endDate}`;
                }
                // Skip default appointment date range if widget has its own date filter or skipDateFilter is set
                const hasWidgetDateFilter = data.upcomingThisWeek || data.pastNoOutcome || data.skipDateFilter;
                if (!data.shouldGetTaskCounter && !hasWidgetDateFilter && (isAppointmentDate || data.isAppointmentDate)) {
                    // Use provided dates or default to last 90 days for appointment-based widgets
                    if (startDate && endDate) {
                        detail.appointmentDateRange = `${startDate},${endDate}`;
                    } else {
                        detail.appointmentDateRange = getDefaultAppointmentDateRange();
                    }
                }
                if (data?.contactedCount) {
                    detail.contactedCount = data.contactedCount;
                }
                if (data?.isContactedCountNull) {
                    detail.isContactedCountNull = data.isContactedCountNull;
                }
                if (data?.contactedCountForStatus) {
                    detail.contactedCountForStatus = data.contactedCountForStatus;
                }
                if (data.clinCheckStatus) {
                    detail.clinCheckStatus = data.clinCheckStatus;
                }
                if (data.isClinCheckTable) {
                    detail.isClinCheckTable = data.isClinCheckTable;
                }
                if (data.reminderContactedCount) {
                    detail.reminderContactedCount = data.reminderContactedCount;
                }
                if (data.group__in) {
                    detail.group__in = data.group__in;
                }
                if (data.completedFilter) {
                    detail.completedFilter = data.completedFilter;
                    detail.is_completed = data.is_completed;
                }
                if (data.appStatus) {
                    detail.appStatus = data.appStatus;
                }
                if (data.appStatusForPrice) {
                    detail.appStatusForPrice = data.appStatusForPrice;
                }
                if (data.treatmentStatusForPrice) {
                    detail.treatmentStatusForPrice = data.treatmentStatusForPrice;
                }
                if (data.pendingWhatsapp) {
                    detail.pendingWhatsapp = data.pendingWhatsapp
                }
                if (data.status__in) {
                    detail.status__in = data.status__in;
                }
                if (data.isTaskDateTimeRange && startDate && endDate) {
                    detail.isTaskDateTimeRange = data.isTaskDateTimeRange;
                    detail.taskDateRange = `${startDate},${endDate}`;
                }
                if (data?.hasNewOrNullTreatment) {
                    detail.hasNewOrNullTreatment = data?.hasNewOrNullTreatment;
                }
                if (data?.healthCareProvider) {
                    detail.healthCareProvider = data?.healthCareProvider;
                }
                // Support for booked table widget filters
                if (data.upcomingThisWeek) {
                    detail.upcomingThisWeek = data.upcomingThisWeek;
                }
                if (data.pastNoOutcome) {
                    detail.pastNoOutcome = data.pastNoOutcome;
                }
                const treatment = getTreatmentId(data.treatment);
                if (data.treatment) {
                    if (treatment) {
                        detail.treatment = treatment;
                    } else {
                        detail.isTreatmentNotPresent = true;
                    }
                }
                if (taskFilter) {
                    if (data.shouldGetPrice) {
                        detail.treatmentPrice = data.shouldGetPrice;
                        dispatch(getTreatmentWidgetPrice(detail));
                        return;
                    }
                    detail.taskFilter = taskFilter
                    detail.dateFilters = data?.dateFilters;
                    dispatch(getTaskWidgetCounter(detail));
                    return;
                }
                if (data.shouldGetPrice) {
                    detail.treatmentPrice = data.shouldGetPrice;
                    dispatch(getTreatmentWidgetPrice(detail));
                    return;
                }
                if (data.shouldGetTaskCounter) {
                    if (data.dateFilters) {
                        detail.dateFilters = data.dateFilters;
                    }
                    dispatch(getTaskWidgetCounter(detail));
                    return;
                }
                if (data.shouldGetReportCounter) {
                    // Use provided dates or default to last 90 days (unless skipDateFilter is set)
                    if (startDate && endDate) {
                        detail.startTime = `${startDate} 00:00:00`;
                        detail.endTime = `${endDate} 23:59:00`;
                    } else if (!data.skipDateFilter) {
                        const defaultRange = getDefaultAppointmentDateRange().split(',');
                        detail.startTime = `${defaultRange[0]} 00:00:00`;
                        detail.endTime = `${defaultRange[1]} 23:59:00`;
                    }
                    // If skipDateFilter is true and no dates provided, no date filter is applied
                    detail.shouldGetReportCounter = data.shouldGetReportCounter;
                    detail.isWidgetCounter = true;
                    dispatch(getReportsAppointmentListing(detail));
                    return;
                }

                dispatch(getWidgetCounter(detail));
            });
        }
    };

    return { reloadWidgetCounter };
}